import { useTranslation } from 'react-i18next';
import type { AiTable as AiTableData } from '@apis/aiAgent';
import { CloudIcon } from '@utils/icons';
import { CONDITION_ICON } from '@utils/weatherIcons';
import type { WeatherConditionCode } from '@interfaces/IWeatherInsights';
import { AiTable } from '../MessageList/AiTable';
import { ExportSelectable, type IExportSelectionProps } from '../MessageList/ExportSelectable';
import { forecastDaysOf } from './forecastDays';
import styles from './forecastStrip.module.css';

/**
 * The forecast as weather rather than as a table: one card per day, side by
 * side, each the way a day is read off a gate board — the day, the sky, how
 * warm and how cold, and whether the weather puts the day's work at risk.
 *
 * It renders the same rows the table would have. The server hands the section
 * a table because that is the shape every section shares; this component reads
 * the forecast back out of it, and if a row is not a forecast day after all it
 * falls back to the table rather than guessing — the brief must never draw
 * weather it was not given.
 */
interface IForecastStripProps extends IExportSelectionProps {
  table: AiTableData;
  /** The brief's own day, so that card can say so. A chat answer has none. */
  today?: string;
}

function formatDay(date: string, locale: string): string {
  const day = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(day.getTime())) return date;
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(day);
}

export function ForecastStrip({ table, today, ...selection }: IForecastStripProps) {
  const { t, i18n } = useTranslation();
  const days = forecastDaysOf(table);

  if (days === null) return <AiTable table={table} {...selection} />;

  return (
    <ExportSelectable {...selection}>
      <ul className={styles.cards}>
        {days.map((day) => {
          const Icon = CONDITION_ICON[day.condition as WeatherConditionCode] ?? CloudIcon;
          return (
            <li key={day.date} className={styles.card} data-risk={day.risk}>
              <span className={styles.when}>
                {day.date === today && (
                  <span className={styles.today}>{t('dashboard.weatherImpact.today')}</span>
                )}
                <time dateTime={day.date}>{formatDay(day.date, i18n.language)}</time>
              </span>
              <span className={styles.sky}>
                <Icon size={22} />
                <span className={styles.temps}>
                  <span className={styles.high}>{day.high}°</span>
                  <span className={styles.low}>{day.low}°</span>
                </span>
              </span>
              <span className={styles.condition}>
                {t(`dashboard.weatherImpact.conditionShort.${day.condition}`, {
                  defaultValue: day.condition,
                })}
              </span>
              <span className={styles.risk}>
                {t('aiAgent.brief.weatherRisk', { level: t(`dashboard.weatherImpact.risk.${day.risk}`) })}
              </span>
            </li>
          );
        })}
      </ul>
    </ExportSelectable>
  );
}
